import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Loading from 'react-simple-loading';
import cd from 'lodash.clonedeep';
import qs from 'querystring';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import Button from 'components/Button/Button';
import { generateID } from 'utils/helpers';
import parseCatch from 'utils/parseCatch';

import { FRIENDSHIP_URL } from './Api';
import styles from './AcceptFriendship.css';


const TRIGGER_SCROLL_THRESHOLD = 200;
const FRIENDSHIP_REQUEST_DELAY = 1200;

const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

class AcceptFriendship extends Component {
  static propTypes = {
    friendRequests: PropTypes.object,
    friendRequestsCounter: PropTypes.number,
    getFriendshipRequests: PropTypes.func,
    setFriendRequestsCounter: PropTypes.func,
    sfw: PropTypes.bool,
    title: PropTypes.string,
    updateFriendshipRequests: PropTypes.func,
  };

  static defaultProps = {
    friendRequests: undefined,
    friendRequestsCounter: undefined,
    getFriendshipRequests: () => undefined,
    setFriendRequestsCounter: () => undefined,
    sfw: undefined,
    title: undefined,
    updateFriendshipRequests: () => undefined,
  };

  constructor(...args) {
    super(...args);

    this.IDS = {
      prefix: 'xa27QFLd',
      element: 'acceptFriendship',
      acceptAll: 'acceptAll',
      rejectAll: 'rejectAll',
      acceptOne: 'acceptOne',
      rejectOne: 'rejectOne',
    };
    this.makeId = generateID(this.IDS.prefix, this.IDS.element);

    this.scrollContainer = React.createRef();
  }

  state = { loading: true };

  componentDidMount() {
    const { friendRequests, friendRequestsCounter } = this.props;
    this.scrollContainer.current.addEventListener('wheel', this.hasScrolled);
    this.scrollContainer.current.addEventListener('scroll', this.hasScrolled);
    if (friendRequests.totalCount <= friendRequestsCounter || friendRequests.size === 0) {
      this.setState({ loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    const { getFriendshipRequests, friendRequestsCounter } = this.props;
    if (prevProps.friendRequestsCounter !== this.props.friendRequestsCounter) {
      getFriendshipRequests(friendRequestsCounter, 0).then(() => this.setState({ loading: false }));
    }
  }

  componentWillUnmount() {
    this.scrollContainer.current.removeEventListener('wheel', this.hasScrolled);
    this.scrollContainer.current.removeEventListener('scroll', this.hasScrolled);
  }

  hasScrolled = (event) => {
    const { friendRequests, friendRequestsCounter, setFriendRequestsCounter } = this.props;
    const nearBottom =
      this.scrollContainer.current.scrollHeight - this.scrollContainer.current.scrollTop <=
      this.scrollContainer.current.clientHeight + 10;
    const unTriggerBottomScroll =
      this.scrollContainer.current.scrollHeight - this.scrollContainer.current.scrollTop <= TRIGGER_SCROLL_THRESHOLD;
    this.setState({ loading: true });

    if (nearBottom && friendRequests.totalCount > friendRequestsCounter) {
      setFriendRequestsCounter();
    }
    if (friendRequests.totalCount <= friendRequestsCounter) {
      this.setState({ loading: false });
    }
    if (event.wheelDelta < 0) {
      if (unTriggerBottomScroll) {
        event.preventDefault();
      }
    }
  };

  onFriendShipRequest = (friendUsername, operation, postMultiParam) => () => {
    const { getFriendshipRequests, friendRequestsCounter, updateFriendshipRequests, friendRequests } = this.props;
    const URL = encodeURI(FRIENDSHIP_URL);
    const postParamSingle = {
      operation,
      friendUsername,
    };
    const postParamAll = {
      operation,
      [operation]: true,
    };
    const postParams = postMultiParam ? postParamAll : postParamSingle;
    const headers = { 'content-type': 'application/x-www-form-urlencoded' };
    this.setState({ [friendUsername]: true });
    const updateFR = updateFriendshipRequests(friendUsername, operation, cd(friendRequests));
    const getFR = new Promise((res) => {
      if (updateFR) {
        res(true);
      }
    });
    const handleRq = ApiService.post(URL, {
      data: qs.stringify(postParams),
      headers,
    });
    Promise.all([getFR, handleRq])
      .then(() => {
        setTimeout(() => {
          getFriendshipRequests(friendRequestsCounter, 0);
        }, FRIENDSHIP_REQUEST_DELAY);
      })
      .catch((e) => parseCatch(e));
  };

  renderFriendShipRequests = () => {
    const { friendRequests, sfw } = this.props;
    const imageClass = !sfw ? styles.friendPic : styles.bluredImage;

    return friendRequests.received.map((request) => (
      <div
        key={request.name}
        className={`${styles.friendWrapper} ${
          request.isDeclined || request.isAccepted ? styles.friendWrapperNoMargin : ''
        }`}
      >
        {request.isAccepted && (
          <div className={`${styles.statusChanged} ${styles.isDisapearing}`}>
            {translate('profile.profile.c4notification.friendaccepted.v2', { 0: request.name })}
          </div>
        )}
        {!request.isAccepted && (
          <div className={`${styles.friendContainerWrap} ${request.isDeclined ? styles.isDisapearing : ''}`}>
            <div className={styles.friendInfo}>
              <img alt="avatar" className={imageClass} src={`${BASE_URL}/avatar/user/60x60/${request.name}.jpg`} />
              <a href={`/${request.name}`}>{request.name}</a>
            </div>
            <div className={styles.interactActions}>
              {this.state[request.name] && !request.isDeclined ? (
                <Loading size="16px" />
              ) : (
                <Fragment>
                  <CheckCircleIcon
                    className={styles.checked}
                    id={this.makeId(this.IDS.acceptOne)}
                    onClick={this.onFriendShipRequest(request.name, 'accept', false)}
                  />
                  <DeleteForeverIcon
                    className={styles.deleted}
                    id={this.makeId(this.IDS.rejectOne)}
                    onClick={this.onFriendShipRequest(request.name, 'ignore', false)}
                  />
                </Fragment>
              )}
            </div>
          </div>
        )}
      </div>
    ));
  };

  render() {
    const { title, friendRequests } = this.props;

    return (
      <div className={styles.acceptFriendship}>
        <h4 className={styles.friendsTitle}>{title}</h4>
        <div
          ref={this.scrollContainer}
          className={`${styles.friendsWrapper} ${friendRequests.totalCount > 0 ? '' : styles.noResult}`}
        >
          {friendRequests.totalCount > 0 ? (
            this.renderFriendShipRequests()
          ) : (
            <span>{translate('strings.ChatRoomManager.noRequests')}</span>
          )}
          {this.state.loading && (
            <div className={styles.loading}>
              <Loading size="16px" />
            </div>
          )}
        </div>
        {friendRequests.totalCount > 0 && (
          <div className={styles.allWrapper}>
            <Button
              className={styles.acceptAll}
              id={this.makeId(this.IDS.acceptAll)}
              label={translate('strings.general.acceptAll')}
              size="small"
              onClick={this.onFriendShipRequest('', 'acceptAll', true)}
            />
            <Button
              className={styles.rejectAll}
              id={this.makeId(this.IDS.rejectAll)}
              label={translate('strings.general.rejectAll')}
              size="small"
              onClick={this.onFriendShipRequest('', 'ignoreAll', true)}
            />
          </div>
        )}
      </div>
    );
  }
}

export default AcceptFriendship;
